import React from "react";
import { BerberStarAccent, BerberTriangleBand } from "@/components/BerberDivider";

/**
 * Moroccan-inspired Polaroid card.
 *
 * Used in the About page timeline to give each Grup Xaluca property a
 * handcrafted, editorial photo strip. The card is intentionally NOT a
 * generic instant-camera polaroid — instead it borrows from southern
 * Moroccan/Amazigh visual language:
 *
 *  • Cream paper stock (#FDFBF7) with a warm copper border and a soft drop
 *    shadow, evoking a hand-stamped hotel postcard.
 *  • A slight per-card tilt (driven by the `tilt` prop) gives the row a
 *    "scrapbook" rhythm without losing alignment.
 *  • A small adhesive-tape vignette at the top centre (BerberTriangleBand)
 *    nods to woven kilim edging — replacing the plain washi tape of a
 *    standard polaroid.
 *  • The photo sits inside a double-line frame (the "khôl" frame seen on
 *    Moroccan miniatures), with four tiny Amazigh star accents anchored
 *    in each corner — a quiet zellige-style flourish.
 *  • The caption block uses the brand serif (font-serif-x) for the year
 *    and small uppercase ink-stamp typography for the property name and
 *    location, mirroring the editorial tone used on the rest of the site.
 *  • The bottom edge is closed with another woven-kilim band so the card
 *    reads as a fully framed object on every screen.
 *
 * Props:
 *  - `image`     URL of the property photo.
 *  - `year`      Display string ("1998", "2024", "Soon"…).
 *  - `title`     Property name.
 *  - `location`  City / region label.
 *  - `tilt`      Rotation in degrees. ±1–2 for a handcrafted feel.
 *  - `tag`       Optional badge text (e.g. "Under construction").
 *  - `testid`    Optional data-testid override for QA.
 *  - `scope`     Optional CMS scope (e.g. "about.timeline.0"). When set,
 *                the photo becomes an addressable image slot with id
 *                `cms.<scope>.image` — editable from /admin/images and
 *                in-page Image Edit Mode.
 */
const MoroccanPolaroid = ({
  image,
  year,
  title,
  location,
  tilt = 0,
  tag,
  testid,
  scope,
}) => {
  return (
    <div
      className="relative w-[260px] shrink-0"
      style={{ transform: `rotate(${tilt}deg)` }}
      data-testid={testid || "moroccan-polaroid"}
      data-cms-scope={scope || undefined}
    >
      {/* The Polaroid paper. Cream stock, copper border, soft shadow. */}
      <div className="relative w-full bg-[#FDFBF7] pt-7 pb-4 px-4 shadow-[0_18px_36px_-18px_rgba(28,22,19,0.35)] border border-[#D4A373]/40">
        {/* Kilim adhesive-tape vignette — sits across the top of the card. */}
        <div
          className="absolute -top-[10px] left-1/2 -translate-x-1/2 w-24 h-5 bg-[#D4A373]/15 border-y border-[#D4A373]/40 overflow-hidden flex items-center justify-center"
          aria-hidden="true"
        >
          <BerberTriangleBand tone="umber" className="opacity-80" height={6} />
        </div>

        {/* Photo + Moorish khôl double-line frame */}
        <div
          className="relative overflow-hidden aspect-square ring-1 ring-[#2C2621]/15"
          data-testid="moroccan-polaroid-photo"
        >
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            data-cms-role={scope ? "image" : undefined}
          />
          {/* Inner khôl frame — thin double line inset from the photo edge */}
          <span
            aria-hidden="true"
            className="absolute inset-[6px] border border-[#FDFBF7]/85 pointer-events-none"
          />
          <span
            aria-hidden="true"
            className="absolute inset-[10px] border border-[#FDFBF7]/40 pointer-events-none"
          />

          {/* Four corner Amazigh stars — zellige-style flourish */}
          <BerberStarAccent
            tone="cream"
            size={14}
            className="absolute top-2 left-2 opacity-90"
          />
          <BerberStarAccent
            tone="cream"
            size={14}
            className="absolute top-2 right-2 opacity-90"
          />
          <BerberStarAccent
            tone="cream"
            size={14}
            className="absolute bottom-2 left-2 opacity-90"
          />
          <BerberStarAccent
            tone="cream"
            size={14}
            className="absolute bottom-2 right-2 opacity-90"
          />

          {/* Optional tag (e.g. "Under construction") */}
          {tag && (
            <span
              className="absolute top-3 left-1/2 -translate-x-1/2 text-[9px] tracking-[0.3em] uppercase px-2.5 py-1 bg-[#FDFBF7]/90 text-[#C16542] border border-[#D4A373]/50"
              data-testid="moroccan-polaroid-tag"
            >
              {tag}
            </span>
          )}
        </div>

        {/* Caption — hand-stamped editorial style */}
        <div className="mt-3 px-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="font-serif-x text-2xl leading-none text-[#C16542] tabular-nums">
              {year}
            </p>
            <p className="text-[9px] tracking-[0.28em] uppercase text-[#5C5248] truncate text-right" title={location}>
              {location}
            </p>
          </div>
          <p className="mt-1.5 font-serif-x text-[15px] leading-snug text-[#2C2621] line-clamp-2 min-h-[2.6em]">
            {title}
          </p>
        </div>

        {/* Closing kilim band at the bottom of the card */}
        <div className="mt-3 px-1" aria-hidden="true">
          <BerberTriangleBand tone="gold" className="opacity-70" height={6} />
        </div>
      </div>
    </div>
  );
};

export default MoroccanPolaroid;
